/**
 * Aanar native-runtime helpers.
 *
 * Detects Capacitor iOS/Android and provides native APIs (haptics, share,
 * push, status bar, prefs, splash, back button). Every helper degrades to a
 * web fallback so the same React code works in the PWA and the native app.
 */
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { Share } from "@capacitor/share";
import { StatusBar, Style } from "@capacitor/status-bar";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Preferences } from "@capacitor/preferences";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";

const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform();

export const native = {
  isNative,
  platform,

  async haptic(kind = "light") {
    if (isNative) {
      try {
        if (["success", "warning", "error"].includes(kind)) {
          const map = { success: "SUCCESS", warning: "WARNING", error: "ERROR" };
          await Haptics.notification({ type: map[kind] });
        } else {
          const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
          await Haptics.impact({ style: map[kind] || ImpactStyle.Light });
        }
      } catch { /* silent */ }
      return;
    }
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      const patterns = {
        light: 10, medium: 20, heavy: 40,
        success: [10, 30, 10], warning: [30, 60, 30], error: [60, 30, 60, 30, 60],
      };
      try { navigator.vibrate(patterns[kind] || 10); } catch { /* silent */ }
    }
  },

  async share(payload) {
    if (isNative) {
      try { await Share.share(payload); return true; } catch { return false; }
    }
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share(payload); return true; } catch { return false; }
    }
    if (payload.url && typeof navigator !== "undefined" && navigator.clipboard) {
      try { await navigator.clipboard.writeText(payload.url); return true; } catch { return false; }
    }
    return false;
  },

  async openExternal(url) {
    if (isNative) {
      try { await Browser.open({ url }); return; } catch { /* fall through */ }
    }
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
  },

  async setStatusBarColor(color, dark = true) {
    if (!isNative) return;
    try {
      await StatusBar.setBackgroundColor({ color });
      await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
    } catch { /* silent */ }
  },

  async pref(key, value) {
    if (value === undefined) {
      if (isNative) return (await Preferences.get({ key })).value;
      return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
    }
    if (isNative) { await Preferences.set({ key, value }); return; }
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  },

  async registerPush(onToken, onNotification) {
    if (!isNative) return false;
    try {
      const perm = await PushNotifications.requestPermissions();
      if (perm.receive !== "granted") return false;
      await PushNotifications.register();
      PushNotifications.addListener("registration", (t) => onToken && onToken(t.value));
      if (onNotification) PushNotifications.addListener("pushNotificationReceived", onNotification);
      return true;
    } catch { return false; }
  },

  async hideSplash() {
    if (!isNative) return;
    try { await SplashScreen.hide(); } catch { /* silent */ }
  },

  onBackButton(handler) {
    if (!isNative) return () => {};
    const p = App.addListener("backButton", async () => {
      const handled = await handler();
      if (!handled) App.exitApp();
    });
    return () => { p.then((h) => h.remove()); };
  },
};
